import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdCheck } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { FaPlus } from 'react-icons/fa';
import { useCurrency } from '../../../../../../../context/currencyContext';
import './ShippingTypes.css';

// Reference image mein Price column nahi hai.
const SHOW_PRICE = false;

const ShippingTypes = () => {
  const navigate = useNavigate();
  const { formatAmount } = useCurrency();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: 'service',
    direction: 'asc',
  });

  const [userLabId, setUserLabId] = useState('');

  const itemsPerPage = 3;

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [pendingDelete, setPendingDelete] = useState({
    id: null,
    service: '',
  });

  // Get user lab ID
  useEffect(() => {
    const labId = localStorage.getItem('userLabId');

    if (labId) {
      setUserLabId(labId);
    }
  }, []);

  // Fetch shipping types
  useEffect(() => {
    const fetchData = async () => {
      if (!userLabId) {
        setLoading(false);
        return;
      }

      try {
        const url = `https://hms.backend.hiragroupcloud.com/api/shipping?addedBy=${userLabId}`;

        const res = await fetch(url);
        const json = await res.json();

        if (json.success) {
          setData(json.data || []);
        } else {
          alert('Failed to load shipping types');
        }
      } catch (err) {
        alert('Network error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userLabId]);

  // Delete
  const handleDelete = async (id, service) => {
    setPendingDelete({
      id,
      service,
    });

    setShowDeleteModal(true);
    setOpenDropdown(null);
  };

  // Confirm delete
  const confirmDelete = async () => {
    try {
      const res = await fetch(
        `https://hms.backend.hiragroupcloud.com/api/shipping/${pendingDelete.id}`,
        {
          method: 'DELETE',
        }
      );

      const json = await res.json();

      if (json.success) {
        setData((prev) =>
          prev.filter(
            (item) => item._id !== pendingDelete.id
          )
        );

        setShowDeleteModal(false);
        setShowSuccessModal(true);
      } else {
        alert('Delete failed');
        setShowDeleteModal(false);
      }
    } catch (err) {
      alert('Delete failed');
      setShowDeleteModal(false);
    }
  };

  // Assign to Accounts
  const handleAssignToAccounts = (item) => {
    navigate(`/assign-accounts/${item._id}`, {
      state: {
        shippingType: item.service,
        shippingId: item._id,
      },
    });
  };

  // Sorting
  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;

    if (sortConfig.key === 'price') {
      const aValue = parseFloat(a[sortConfig.key]) || 0;
      const bValue = parseFloat(b[sortConfig.key]) || 0;

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }

      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }

      return 0;
    }

    const aValue =
      a[sortConfig.key]?.toString().toLowerCase() || '';

    const bValue =
      b[sortConfig.key]?.toString().toLowerCase() || '';

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }

    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }

    return 0;
  });

  // Search
  const filteredData = sortedData.filter(
    (item) =>
      item.service
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      item.company
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(
    totalItems / itemsPerPage
  );

  const startIndex =
    (currentPage - 1) * itemsPerPage;

  const endIndex = Math.min(
    startIndex + itemsPerPage,
    totalItems
  );

  const currentData = filteredData.slice(
    startIndex,
    endIndex
  );

  // Search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Pagination
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Add
  const handleAddClick = () => {
    navigate('/AddShippingType');
  };

  // Dropdown
  const toggleDropdown = (id) => {
    setOpenDropdown(
      openDropdown === id ? null : id
    );
  };

  // Edit
  const handleEditClick = (item) => {
    if (item.addedBy !== userLabId) {
      alert(
        'You can only edit your own shipping types'
      );
      return;
    }

    navigate(`/shipping-edit/${item._id}`);
  };

  // Sort
  const handleSort = (key) => {
    let direction = 'asc';

    if (
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }

    setSortConfig({
      key,
      direction,
    });
  };

  // Sort arrows
  const getSortIndicator = (key) => {
    const isActive = sortConfig.key === key;

    return (
      <span className="st-sort">
        <span
          className={`up ${
            isActive &&
            sortConfig.direction === 'asc'
              ? 'active'
              : ''
          }`}
        />

        <span
          className={`down ${
            isActive &&
            sortConfig.direction === 'desc'
              ? 'active'
              : ''
          }`}
        />
      </span>
    );
  };

  // Format price
  const formatPrice = (price) => {
    if (
      price === null ||
      price === undefined ||
      price === ''
    ) {
      return formatAmount(0);
    }

    const numericPrice =
      typeof price === 'string'
        ? parseFloat(price)
        : price;

    return formatAmount(
      isNaN(numericPrice) ? 0 : numericPrice
    );
  };

  // Close dropdown outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !e.target.closest('.st-dropdown-container')
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };
  }, []);

  const headers = [
    {
      key: 'service',
      label: 'Service',
    },
    {
      key: 'company',
      label: 'Company',
    },
    {
      key: 'inboundDays',
      label: 'Inbound Transit Days',
    },
    {
      key: 'outboundDays',
      label: 'Outbound Transit Days',
    },
    ...(SHOW_PRICE
      ? [
          {
            key: 'price',
            label: 'Price',
          },
        ]
      : []),
    {
      key: null,
      label: '',
    },
  ];

  if (loading) {
    return (
      <div className="st-loading">
        Loading...
      </div>
    );
  }

  if (!userLabId) {
    return (
      <div className="st-no-user">
        <h1 className="st-title">
          Shipping Types
        </h1>

        <div className="st-error">
          User ID not found. Please log in again.
        </div>
      </div>
    );
  }

  return (
    <div className="shipping-types-page">

      <h1 className="st-title">
        Shipping Types
      </h1>

      <div className="st-panel">
        <div className="st-card">

          {/* SEARCH + ADD */}
          <div className="st-top">

            <div className="st-search-wrap">
              <label className="st-search-label">
                Search
              </label>

              <input
                type="text"
                className="st-search-input"
                placeholder="Type to search"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            <button
              type="button"
              className="st-add-btn"
              onClick={handleAddClick}
            >
              <FaPlus />
              <span>Add</span>
            </button>

          </div>

          {/* TABLE */}
          <div className="st-table-wrap">

            <table className="st-table">

              <thead>
                <tr>

                  {headers.map((header, index) => (
                    <th
                      key={index}
                      className={
                        header.key
                          ? ''
                          : 'st-th-plain'
                      }
                      onClick={
                        header.key
                          ? () =>
                              handleSort(
                                header.key
                              )
                          : undefined
                      }
                    >

                      <div className="st-th-inner">

                        <span>
                          {header.label}
                        </span>

                        {header.key &&
                          getSortIndicator(
                            header.key
                          )}

                      </div>

                    </th>
                  ))}

                </tr>
              </thead>

              <tbody>

                {currentData.length > 0 ? (

                  currentData.map((item) => (
                    <tr key={item._id}>

                      <td>
                        {item.service}
                      </td>

                      <td>
                        {item.company}
                      </td>

                      <td>
                        {item.inboundDays}
                      </td>

                      <td>
                        {item.outboundDays} Days
                      </td>

                      {SHOW_PRICE && (
                        <td>
                          {formatPrice(item.price)}
                        </td>
                      )}

                      <td className="st-actions-cell">

                        <div className="st-dropdown-container">

                          <div className="st-dropdown">

                            <button
                              type="button"
                              className="st-edit-btn"
                              onClick={() =>
                                handleEditClick(
                                  item
                                )
                              }
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className="st-caret-btn"
                              onClick={() =>
                                toggleDropdown(
                                  item._id
                                )
                              }
                              aria-label="Open actions"
                            >
                              <span className="st-caret" />
                            </button>

                          </div>

                          {openDropdown ===
                            item._id && (
                            <div className="st-menu">

                              <button
                                type="button"
                                className="st-menu-item"
                                onClick={() => {
                                  handleAssignToAccounts(
                                    item
                                  );

                                  setOpenDropdown(
                                    null
                                  );
                                }}
                              >
                                <MdCheck />

                                <span>
                                  Assign To Accounts
                                </span>
                              </button>

                              <button
                                type="button"
                                className="st-menu-item danger"
                                onClick={() =>
                                  handleDelete(
                                    item._id,
                                    item.service
                                  )
                                }
                              >
                                <RiDeleteBin6Line />

                                <span>
                                  Delete
                                </span>
                              </button>

                            </div>
                          )}

                        </div>

                      </td>

                    </tr>
                  ))

                ) : (

                  <tr>
                    <td
                      colSpan={headers.length}
                      className="st-empty"
                    >
                      No shipping types found.
                      Click "Add" to create one.
                    </td>
                  </tr>

                )}

              </tbody>

            </table>

          </div>

          {/* FOOTER */}
          {totalItems > 0 && (
            <div className="st-footer">

              <div className="st-showing">
                Showing {startIndex + 1} to{' '}
                {endIndex} of {totalItems}{' '}
                entries
              </div>

              <div className="st-pagination">

                <button
                  type="button"
                  className="st-page-btn"
                  onClick={() =>
                    goToPage(1)
                  }
                  disabled={currentPage === 1}
                >
                  First
                </button>

                <button
                  type="button"
                  className="st-page-btn"
                  onClick={() =>
                    goToPage(currentPage - 1)
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {Array.from(
                  {
                    length: Math.min(
                      totalPages,
                      5
                    ),
                  },
                  (_, index) => {
                    const page = index + 1;

                    return (
                      <button
                        type="button"
                        key={page}
                        className={`st-page-btn ${
                          currentPage === page
                            ? 'active'
                            : ''
                        }`}
                        onClick={() =>
                          goToPage(page)
                        }
                      >
                        {page}
                      </button>
                    );
                  }
                )}

                <button
                  type="button"
                  className="st-page-btn"
                  onClick={() =>
                    goToPage(currentPage + 1)
                  }
                  disabled={
                    currentPage === totalPages
                  }
                >
                  Next
                </button>

                <button
                  type="button"
                  className="st-page-btn"
                  onClick={() =>
                    goToPage(totalPages)
                  }
                  disabled={
                    currentPage === totalPages
                  }
                >
                  Last
                </button>

              </div>

            </div>
          )}

        </div>
      </div>

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div className="st-modal-overlay">

          <div className="st-modal">

            <div className="st-warning-icon">
              <span>!</span>
            </div>

            <h2>
              Confirm Deletion
            </h2>

            <p>
              Are you sure you want to delete
              shipping type "
              <strong>
                {pendingDelete.service}
              </strong>
              "?
            </p>

            <div className="st-modal-buttons">

              <button
                type="button"
                onClick={() =>
                  setShowDeleteModal(false)
                }
                className="st-modal-no"
              >
                No
              </button>

              <button
                type="button"
                onClick={confirmDelete}
                className="st-modal-delete"
              >
                Yes, Delete
              </button>

            </div>

          </div>

        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="st-modal-overlay">

          <div className="st-modal">

            <div className="st-success-icon">
              <span>✓</span>
            </div>

            <h2>
              Success!
            </h2>

            <p>
              Shipping type deleted
              successfully!
            </p>

            <button
              type="button"
              onClick={() =>
                setShowSuccessModal(false)
              }
              className="st-modal-ok"
            >
              OK
            </button>

          </div>

        </div>
      )}

    </div>
  );
};

export default ShippingTypes;